import React, { useState, useEffect } from 'react';
import { Alert, Button, Row, Col, Form, Modal } from '@/components/ui';
import { VehicleProfile, VehicleProfileFormData } from '../../../types/Vehicle';
import vehicleProfileService from '../../../services/vehicleProfileService';
import { describeActionError } from '../../../utils/loadError';

interface VehicleProfileFormProps {
  profile: VehicleProfile | null;
  onClose: () => void;
}

const VehicleProfileForm: React.FC<VehicleProfileFormProps> = ({ profile, onClose }) => {
  const [formData, setFormData] = useState<VehicleProfileFormData>({
    inspection: '',
    vehicleType: '',
    vehicleClassification: '',
    pmClassification: '',
    odometerAlerts: '',
    mandatoryOdometerPicture: false,
    requiresCVIExpiryDate: false,
    requiresOdometerReading: false,
    requiresBrakeInspection: false,
    requiresPostTrip: false,
    requiresForemanApproval: false,
    requiresMechSupervisorApproval: false,
    postTripThreshold: undefined
  });
  const [saving, setSaving] = useState(false);
  const [inspectionError, setInspectionError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (profile) {
      setFormData({
        inspection: profile.inspection,
        vehicleType: profile.vehicleType || '',
        vehicleClassification: profile.vehicleClassification || '',
        pmClassification: profile.pmClassification || '',
        odometerAlerts: profile.odometerAlerts || '',
        mandatoryOdometerPicture: profile.mandatoryOdometerPicture || false,
        requiresCVIExpiryDate: profile.requiresCVIExpiryDate || false,
        requiresOdometerReading: profile.requiresOdometerReading || false,
        requiresBrakeInspection: profile.requiresBrakeInspection || false,
        requiresPostTrip: profile.requiresPostTrip || false,
        requiresForemanApproval: profile.requiresForemanApproval || false,
        requiresMechSupervisorApproval: profile.requiresMechSupervisorApproval || false,
        postTripThreshold: profile.postTripThreshold || undefined
      });
    }
  }, [profile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError(null);
    if (!formData.inspection.trim()) {
      setInspectionError('Inspection name required');
      document.getElementById('vehicleProfileInspection')?.focus();
      return;
    }
    setSaving(true);

    try {
      if (profile) {
        await vehicleProfileService.updateProfile(profile.id, formData);
      } else {
        await vehicleProfileService.createProfile(formData);
      }
      onClose();
    } catch (error: any) {
      console.error('Error saving profile:', error);
      setSaveError(describeActionError(error, "Couldn't save the vehicle profile. Try again."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show onHide={onClose} size="lg" backdrop={saving ? 'static' : true}>
      <Modal.Header closeButton>
        <Modal.Title>{profile ? 'Edit Profile' : 'Add Profile'}</Modal.Title>
      </Modal.Header>
      {/* Form wraps Body+Footer (footer holds the submit) and is the flex column itself. */}
      <form onSubmit={handleSubmit} noValidate className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
            <Modal.Body>
              {saveError && (
                <Alert variant="danger" role="alert" className="tw-mb-3">
                  {saveError}
                </Alert>
              )}
              <Row className="tw-mb-3">
                <Col md={6}>
                  <Form.Label htmlFor="vehicleProfileInspection">Inspection Name *</Form.Label>
                  <Form.Control
                    id="vehicleProfileInspection"
                    type="text"
                    value={formData.inspection}
                    onChange={(e) => {
                      setFormData({ ...formData, inspection: e.target.value });
                      setInspectionError(null);
                    }}
                    required
                    disabled={!!profile}
                    isInvalid={!!inspectionError}
                  />
                  {inspectionError && <Form.Control.Feedback type="invalid">{inspectionError}</Form.Control.Feedback>}
                </Col>
                <Col md={6}>
                  <Form.Label>Vehicle Type</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.vehicleType}
                    onChange={(e) => setFormData({ ...formData, vehicleType: e.target.value })}
                  />
                </Col>
              </Row>
              <Row className="tw-mb-3">
                <Col md={6}>
                  <Form.Label>Vehicle Classification</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.vehicleClassification}
                    onChange={(e) => setFormData({ ...formData, vehicleClassification: e.target.value })}
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>PM Classification</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.pmClassification}
                    onChange={(e) => setFormData({ ...formData, pmClassification: e.target.value })}
                  />
                </Col>
              </Row>
              <Row className="tw-mb-3">
                <Col md={6}>
                  <Form.Label>Odometer Alerts</Form.Label>
                  <Form.Select
                    value={formData.odometerAlerts || ''}
                    onChange={(e) => setFormData({ ...formData, odometerAlerts: e.target.value || undefined })}
                  >
                    <option value="">Select...</option>
                    <option value="KM">KM</option>
                    <option value="Hours">Hours</option>
                  </Form.Select>
                </Col>
                <Col md={6}>
                  <Form.Label>Post-trip Threshold</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.postTripThreshold || ''}
                    onChange={(e) => setFormData({ ...formData, postTripThreshold: e.target.value ? parseInt(e.target.value) : undefined })}
                  />
                </Col>
              </Row>
              <Row className="tw-mb-3">
                <Col md={6}>
                  <Form.Check
                    type="checkbox"
                    checked={formData.mandatoryOdometerPicture || false}
                    onChange={(e) => setFormData({ ...formData, mandatoryOdometerPicture: e.target.checked })}
                    label="Mandatory Odometer Picture"
                  />
                  <Form.Check
                    type="checkbox"
                    checked={formData.requiresCVIExpiryDate || false}
                    onChange={(e) => setFormData({ ...formData, requiresCVIExpiryDate: e.target.checked })}
                    label="Requires CVI Expiry Date"
                  />
                  <Form.Check
                    type="checkbox"
                    checked={formData.requiresOdometerReading || false}
                    onChange={(e) => setFormData({ ...formData, requiresOdometerReading: e.target.checked })}
                    label="Requires Odometer Reading"
                  />
                  <Form.Check
                    type="checkbox"
                    checked={formData.requiresBrakeInspection || false}
                    onChange={(e) => setFormData({ ...formData, requiresBrakeInspection: e.target.checked })}
                    label="Requires Brake Inspection"
                  />
                </Col>
                <Col md={6}>
                  <Form.Check
                    type="checkbox"
                    checked={formData.requiresPostTrip || false}
                    onChange={(e) => setFormData({ ...formData, requiresPostTrip: e.target.checked })}
                    label="Requires Post-trip"
                  />
                  <Form.Check
                    type="checkbox"
                    checked={formData.requiresForemanApproval || false}
                    onChange={(e) => setFormData({ ...formData, requiresForemanApproval: e.target.checked })}
                    label="Requires Foreman Approval"
                  />
                  <Form.Check
                    type="checkbox"
                    checked={formData.requiresMechSupervisorApproval || false}
                    onChange={(e) => setFormData({ ...formData, requiresMechSupervisorApproval: e.target.checked })}
                    label="Requires Mech Supervisor Approval"
                  />
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Modal.Footer>
          </form>
    </Modal>
  );
};

export default VehicleProfileForm;

